<template>
    <GetStartedLayout
        :errors="errors"
        :guest="guest"
        :child="child"
        :helpForm="false"
        :locations="locations"
        :location="locations.find((e) => e.id == form.location_id)"
        :review="review"
        :stepNumber="stepNumber"
        hideFab
        :title="currentStep.title"
        :subtitle="
            currentStep.name == 'form'
                ? `Risk free with our
                <a
                    href='https://musicologielessons.com/why-choose-musicologie'
                    target='_blank'
                    class='white--text'
                >
                    Perfect Match Promise</a
                >`
                : currentStep.subtitle
        "
    >
        <v-window
            v-model="stepNumber"
            class="elevation-0 w-full pa-0"
            style="background: transparent"
            touchless
        >
            <div v-for="step in stepData" :key="step.name">
                <v-window-item
                    :step="stepData.indexOf(step)"
                    class="px-3 custom-stepper"
                    style="overflow: visible"
                >
                    <component
                        v-if="currentStep.name == step.name"
                        :key="step.name"
                        :is="step.comp"
                        v-bind="{ ...step.props() }"
                        v-on="step.on"
                        v-model="form[step.name]"
                        :class="step.classes"
                    ></component>
                </v-window-item>
            </div>
        </v-window>

        <WindowMarker
            v-model="stepNumber"
            :length="stepData.length"
            class="mt-1"
            v-if="stepNumber"
            @input="clearFutureSteps($event)"
        />
    </GetStartedLayout>
</template>

<script>
import GetStartedLayout from "../../Layouts/GetStartedLayout.vue";
import { Link } from "@inertiajs/vue2";
import FullscreenCards from "../../Components/FullscreenCards.vue";
import LocationList from "./LocationList.vue";
import InstrumentList from "./InstrumentList.vue";
import WindowMarker from "./WindowMarker.vue";
import CreateGuestForm from "../../Components/GetStarted/CreateGuestForm.vue";
import "../../../../Plugins/gtm.js";
import "../../../../Plugins/fbPixel.js";

export default {
    components: {
        GetStartedLayout,
        Link,
        CreateGuestForm,
        FullscreenCards,
        LocationList,
        InstrumentList,
        WindowMarker,
    },
    props: {
        errors: {},
        requireLastNames: {
            type: Boolean,
            default: false,
        },
        includeOnline: {
            type: Boolean,
            default: false,
        },
        contactOnly: {
            type: Boolean,
            default: false,
        },
        guest: {
            type: Object,
            default: null,
        },
        child: {
            type: Object,
            default: null,
        },
        instruments: {
            type: Array,
            default: () => [],
        },
        locations: {
            type: Array,
            default: () => [],
        },
        location_id: {
            type: Number,
            default: null,
        },
        instrument_id: {
            type: Number,
            default: null,
        },
        review: {
            type: Object,
            default: null,
        },
        hasChild: {
            type: Boolean,
            default: null,
        },
        promo: {
            type: String,
            default: null,
        },
        source: {
            type: String,
            default: null,
        },
    },
    name: "CreateGuest",
    remember: {
        data: ["form"],
        key: "GetStarted/CreateGuest",
    },
    data() {
        return {
            form: {
                hasChild: this.hasChild ?? null,
                location_id: this.location_id ?? null,
                instrument_id: this.instrument_id ?? null,
                form: null,
            },
            stepNumber: 0,
            stepData: [
                {
                    name: "hasChild",
                    step: 0,
                    title: "Music Makes Us Human",
                    subtitle: "What kind of lessons are you looking for?",
                    comp: "FullscreenCards",
                    props: () => ({
                        cards: [
                            {
                                title: "Adult Lessons",
                                subtitle: "Lessons For Myself",
                                src: "/adult-guitar-lesson.webp",
                                value: false,
                            },
                            {
                                title: "Kids Lessons",
                                subtitle: "Lessons For My Child",
                                src: "/apl-assessment-tiny.webp",
                                value: true,
                            },
                        ],
                    }),
                    on: {
                        input: (e) => {
                            this.form.hasChild = e;
                            history.pushState(
                                this.form,
                                "Get Started",
                                "/get-started"
                            );
                            this.nextStep();
                        },
                    },
                },
                {
                    name: "location_id",
                    step: 1,
                    title: "Which location do you prefer?",
                    comp: "LocationList",
                    props: () => ({
                        returnObject: false,
                        parentLocations: this.locations,
                    }),
                    on: {
                        input: (e) => {
                            history.pushState(
                                this.form,
                                "Get Started",
                                "/get-started"
                            );
                            this.nextStep();
                        },
                    },
                },
                {
                    name: "instrument_id",
                    step: 2,
                    title: "Which instrument do you want to learn?",
                    comp: "InstrumentList",
                    props: () => ({
                        returnObject: false,
                        parentInstruments: this.instruments,
                    }),
                    on: {
                        input: (e) => {
                            history.pushState(
                                this.form,
                                "Get Started",
                                "/get-started"
                            );
                            this.nextStep();
                        },
                    },
                },
                {
                    name: "form",
                    step: 3,
                    comp: "CreateGuestForm",
                    title: "Schedule Your First Lesson",
                    props: () => ({
                        errors: this.errors,
                        requireLastNames: this.requireLastNames,
                        includeOnline: this.includeOnline,
                        contactOnly: this.contactOnly,
                        guest: this.guest,
                        child: this.child,
                        instruments: this.instruments,
                        locations: this.locations,
                        location_id: this.form.location_id,
                        instrument_id: this.form.instrument_id,
                        hasChild: this.form.hasChild,
                        promo: this.promo,
                        source: this.getSource(),
                    }),
                },
            ],
        };
    },
    computed: {
        currentStep() {
            return this.stepData[this.stepNumber];
        },
        currentLocation() {
            return (
                this.locations.find((e) => e.id == this.form.location_id) ??
                null
            );
        },
    },
    methods: {
        nextStep() {
            this.stepNumber = Object.values(this.stepData).find((e) => {
                return this.form[e.name] == null;
            }).step;
        },
        clearFutureSteps(step) {
            this.stepData.forEach((e) => {
                if (e.step >= step) this.form[e.name] = null;
            });
        },
        updateLocationDataLayer() {
            if (!this.currentLocation) return;
            if (window.dataLayer.find((e) => e.location)) return;
            window.dataLayer.push({
                location: this.currentLocation.slug,
                session_id: window.Laravel?.session_id,
            });
        },
        getSource() {
            if (this.source) return this.source;
            let tags = window.location.search;

            if (tags.includes("utm_source")) {
                return tags.split("?utm_source=")[1].split("&")[0];
            }
            if (tags.includes("fbclid")) return "meta_ads";
            if (tags.includes("gclid")) return "google_ads";
        },
    },
    mounted() {
        if (this.form.location_id) this.updateLocationDataLayer();
        this.nextStep();
    },
    watch: {
        form: {
            handler: function (form) {
                if (form.location_id) this.updateLocationDataLayer();
            },
            deep: true,
        },
    },
};
</script>
